import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import { ChevronLeft, Plus, Trash2, Search, ScanLine, Wine, UtensilsCrossed } from 'lucide-react';
import BarcodeScanner from '../../components/BarcodeScanner';
import { searchLiquorProducts, createPurchase } from '../../services/purchaseService';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Record a new purchase; liquor is built end to end, and saving stocks each line into Bar Inventory. */
export default function NewPurchase() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [purchaseType, setPurchaseType] = useState('liquor');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [supplier, setSupplier] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [note, setNote] = useState('');

  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [searching, setSearching] = useState(false);
  // Per-dish draft input keyed by dish_id: { variant_id, quantity, unit_cost }.
  const [drafts, setDrafts] = useState({});
  const [scanOpen, setScanOpen] = useState(false);

  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (!restaurantId || !locationId) return;
    setSearching(true);
    try {
      const rows = await searchLiquorProducts(restaurantId, locationId, { q });
      setProducts(rows);
      setDrafts((prev) => {
        const next = {};
        rows.forEach((d) => {
          const firstVariant = d.variants[0];
          next[d.dish_id] = prev[d.dish_id] || {
            variant_id: firstVariant ? String(firstVariant.variant_id) : '',
            quantity: '1',
            unit_cost: d.cost_per_bottle ? String(d.cost_per_bottle) : '',
          };
        });
        return next;
      });
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to search products', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  }, [restaurantId, locationId, toast]);

  // Debounced search as the user types (liquor only).
  useEffect(() => {
    if (purchaseType !== 'liquor') return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(query.trim()), 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [query, purchaseType, runSearch]);

  const setDraft = (dishId, patch) =>
    setDrafts((prev) => ({ ...prev, [dishId]: { ...prev[dishId], ...patch } }));

  const addLine = (dish) => {
    const draft = drafts[dish.dish_id] || {};
    const qty = Number(draft.quantity);
    if (!qty || qty <= 0) {
      toast({ title: 'Quantity required', description: 'Enter a quantity greater than 0.', variant: 'destructive' });
      return;
    }
    const variant = dish.variants.find((v) => String(v.variant_id) === String(draft.variant_id));
    setLines((prev) => [
      ...prev,
      {
        key: `${dish.dish_id}-${draft.variant_id}-${prev.length}`,
        dish_id: dish.dish_id,
        dish_name: dish.name,
        variant_id: variant ? variant.variant_id : null,
        variant_name: variant ? variant.variant_name : '',
        quantity: qty,
        unit_cost: Number(draft.unit_cost) || 0,
      },
    ]);
  };

  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unit_cost, 0);

  const onScanned = (code) => {
    if (!code) return;
    // Match a scanned barcode to a product variant already in the results.
    for (const d of products) {
      const v = d.variants.find((x) => x.barcode && x.barcode === code);
      if (v) {
        setDraft(d.dish_id, { variant_id: String(v.variant_id) });
        toast({ title: 'Matched', description: `${d.name} · ${v.variant_name}` });
        setScanOpen(false);
        return;
      }
    }
    // Otherwise drop the code into the search box.
    setQuery(code);
    toast({ title: 'No match', description: 'Searching by the scanned code.' });
  };

  const save = async () => {
    if (!invoiceNumber.trim()) {
      toast({ title: 'Invoice required', description: 'Enter the supplier invoice number.', variant: 'destructive' });
      return;
    }
    if (lines.length === 0) {
      toast({ title: 'No items', description: 'Add at least one product to the purchase.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await createPurchase(restaurantId, locationId, {
        purchase_type: purchaseType,
        invoice_number: invoiceNumber.trim(),
        supplier: supplier.trim(),
        purchase_date: purchaseDate,
        note: note.trim(),
        lines: lines.map((l) => ({
          dish_id: l.dish_id,
          variant_id: l.variant_id,
          quantity: l.quantity,
          unit_cost: l.unit_cost,
        })),
      });
      toast({ title: 'Purchase saved', description: 'Liquor lines have been stocked in.' });
      navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase`);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to save the purchase', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Purchases
      </button>

      <div className="mb-8">
        <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">New Purchase</h1>
      </div>

      {/* Type + header */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Purchase details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Type</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPurchaseType('liquor')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm transition-colors ${
                  purchaseType === 'liquor'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-input text-muted-foreground hover:bg-accent'
                }`}
              >
                <Wine className="h-4 w-4" /> Liquor
              </button>
              <button
                type="button"
                disabled
                title="Food purchasing is coming soon"
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-input text-sm text-muted-foreground/60 cursor-not-allowed"
              >
                <UtensilsCrossed className="h-4 w-4" /> Food
                <span className="ml-1 text-[11px] uppercase tracking-wide">Soon</span>
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="invoice">Invoice number *</Label>
              <Input id="invoice" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2045" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input id="supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. United Spirits" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Purchase date</Label>
              <Input id="date" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note">Note (optional)</Label>
            <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any reference for this purchase" />
          </div>
        </CardContent>
      </Card>

      {/* Product search */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Add liquor products</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or brand…"
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={() => setScanOpen(true)}>
              <ScanLine className="h-4 w-4 mr-2" /> Scan
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Size / variant</TableHead>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead className="w-32">Unit cost</TableHead>
                  <TableHead className="w-20 text-right">Add</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searching && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Searching…</TableCell></TableRow>
                )}
                {!searching && products.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No liquor products found.</TableCell></TableRow>
                )}
                {!searching && products.map((d) => {
                  const draft = drafts[d.dish_id] || {};
                  return (
                    <TableRow key={d.dish_id}>
                      <TableCell>
                        <div className="font-medium">{d.name}</div>
                        {d.brand && <div className="text-xs text-muted-foreground">{d.brand}</div>}
                      </TableCell>
                      <TableCell>
                        <select
                          value={draft.variant_id || ''}
                          onChange={(e) => setDraft(d.dish_id, { variant_id: e.target.value })}
                          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        >
                          {d.variants.length === 0 && <option value="">No variants</option>}
                          {d.variants.map((v) => (
                            <option key={v.variant_id} value={v.variant_id}>
                              {v.variant_name}{v.is_full_bottle ? ' (bottle)' : ''}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" min="0" step="any"
                          value={draft.quantity ?? ''}
                          onChange={(e) => setDraft(d.dish_id, { quantity: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" min="0" step="any"
                          value={draft.unit_cost ?? ''}
                          onChange={(e) => setDraft(d.dish_id, { unit_cost: e.target.value })}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => addLine(d)} disabled={d.variants.length === 0}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Lines */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Items on this purchase</CardTitle>
          <CardDescription>{lines.length} line{lines.length === 1 ? '' : 's'}.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit cost</TableHead>
                  <TableHead className="text-right">Line total</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No items added yet.</TableCell></TableRow>
                )}
                {lines.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell className="font-medium">{l.dish_name}</TableCell>
                    <TableCell>{l.variant_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(l.unit_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(l.quantity * l.unit_cost)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeLine(l.key)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {lines.length > 0 && (
                  <TableRow className="bg-slate-50/60">
                    <TableCell colSpan={4} className="text-right font-medium">Total</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtMoney(total)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase`)}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save purchase'}</Button>
      </div>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onScanned} />
    </div>
  );
}
